import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, Check, Eraser, Flame, Hammer, Loader2, Save, Search, UploadCloud } from 'lucide-react';
import { apiService } from '@/services/api.service';
import { useUnsavedDraft } from '@/stores/unsavedDrafts.store';
import { PackThumbnail } from './PackThumbnail';
import { loadPackPalette, PalettePickEntry } from './packPalette';
import styles from './PackForm.module.scss';
import recipe from './RecipeEditor.module.scss';

type CraftType = 'classique' | 'four';
type PaletteFilter = 'all' | 'block' | 'item';

const CRAFT_TYPES = [
  { id: 'classique' as const, icon: Hammer, label: 'Classique (3×3)', desc: 'Table de craft vanilla. Disposition shaped.' },
  { id: 'four' as const, icon: Flame, label: 'Four', desc: 'Four / blast furnace. 1 entrée → 1 sortie + XP.' },
];

const PALETTE_FILTERS: { id: PaletteFilter; label: string }[] = [
  { id: 'all', label: 'Tous' },
  { id: 'block', label: 'Blocks' },
  { id: 'item', label: 'Items' },
];

export function RecipeEditor({
  identifier,
  thumbnail,
  groupId,
  onSaved,
  draftKind = 'craft',
  draftScope = 'main',
}: {
  identifier: string;
  thumbnail?: string | null;
  groupId: string;
  onSaved?: () => void;
  draftKind?: string;
  draftScope?: string;
}) {
  const [craftType, setCraftType] = useState<CraftType>('classique');
  const [dirty, setDirty] = useState(false);
  const [grid, setGrid] = useState<string[]>(Array(9).fill(''));
  const [count, setCount] = useState('1');
  const [input, setInput] = useState('');
  const [experience, setExperience] = useState('0');
  const [brush, setBrush] = useState<string>(''); // '' = gomme
  const [palette, setPalette] = useState<PalettePickEntry[]>([]);
  const [paletteFilter, setPaletteFilter] = useState<PaletteFilter>('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    let active = true;
    loadPackPalette().then((p) => active && setPalette(p));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiService.getPackRecipe(groupId, identifier).then((r) => {
      if (!active) return;
      const d = r.data;
      if (d?.type === 'furnace') {
        setCraftType('four');
        setInput(d.input || '');
        setExperience(String(d.experience ?? 0));
      } else {
        setCraftType('classique');
        setGrid(Array.from({ length: 9 }, (_, i) => d?.grid?.[i] ?? ''));
        setCount(String(d?.count || 1));
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [groupId, identifier]);

  const infoOf = useMemo(() => {
    const m = new Map<string, { path: string | null; groupId: string }>();
    palette.forEach((p) => m.set(p.id, { path: p.path, groupId: p.groupId }));
    return m;
  }, [palette]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let l = paletteFilter === 'all' ? palette : palette.filter((p) => p.kind === paletteFilter);
    if (q) l = l.filter((p) => p.id.toLowerCase().includes(q));
    return l.slice(0, 160);
  }, [palette, query, paletteFilter]);

  const setCell = (i: number, v: string) => { setGrid((g) => g.map((c, idx) => (idx === i ? v : c))); setDirty(true); };
  const gridEmpty = grid.every((c) => !c.trim());
  const canSave = craftType === 'four' ? !!input.trim() : !gridEmpty;

  const save = async (): Promise<boolean> => {
    if (!canSave) { setFeedback({ ok: false, msg: 'Recette vide — rien à enregistrer.' }); return false; }
    setSaving(true);
    setFeedback(null);
    const res = await apiService.savePackRecipe(groupId, {
      identifier: identifier,
      type: craftType === 'four' ? 'furnace' : 'shaped',
      grid,
      count: Number(count) || 1,
      input,
      experience: Number(experience) || 0,
    });
    setSaving(false);
    if (res.data?.success) {
      setDirty(false);
      setFeedback({ ok: true, msg: res.data.message });
      onSaved?.();
      return true;
    }
    setFeedback({ ok: false, msg: res.data?.message || res.error || 'Échec' });
    return false;
  };

  // Suivi des modifications non enregistrées → barre « Enregistrer tout » + marqueur liste.
  useUnsavedDraft({
    kind: draftKind,
    scope: draftScope,
    groupId,
    identifier,
    label: draftScope === 'recipe' ? `${identifier} (recette)` : identifier,
    dirty,
    save,
  });

  const publish = async () => {
    setPublishing(true);
    setFeedback(null);
    const res = await apiService.publishPackGroup(groupId);
    setPublishing(false);
    setFeedback(res.data ? { ok: res.data.success, msg: res.data.messages.join(' · ') } : { ok: false, msg: res.error || 'Échec publication' });
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Loader2 size={18} className={styles.spin} /> Chargement de la recette…
      </div>
    );
  }

  return (
    <div className={styles.form}>
      {/* Type de craft */}
      <div className={recipe.typeCards}>
        {CRAFT_TYPES.map((t) => {
          const Icon = t.icon;
          const active = t.id === craftType;
          return (
            <button
              key={t.id}
              type="button"
              className={`${recipe.typeCard} ${active ? recipe.typeCardActive : ''}`}
              onClick={() => { setCraftType(t.id); setDirty(true); }}
            >
              <Icon size={18} />
              <span className={recipe.typeLabel}>{t.label}</span>
              <span className={recipe.typeDesc}>{t.desc}</span>
            </button>
          );
        })}
      </div>

      {/* Palette blocks + items */}
      <div className={recipe.paletteBox}>
        <div className={recipe.paletteHead}>
          <div className={styles.search}>
            <Search size={14} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un block / item…" />
          </div>
          <div className={recipe.filterTabs}>
            {PALETTE_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`${recipe.filterTab} ${paletteFilter === f.id ? recipe.filterTabActive : ''}`}
                onClick={() => setPaletteFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button type="button" className={`${recipe.eraseBtn} ${brush === '' ? recipe.eraseBtnActive : ''}`} onClick={() => setBrush('')}>
            <Eraser size={14} /> Gomme
          </button>
        </div>
        {filtered.length === 0 && <div className={recipe.paletteEmpty}>Aucun résultat — utilise le champ « Pinceau » pour saisir un id à la main.</div>}
        <div className={recipe.palette}>
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              title={p.id}
              className={`${recipe.paletteItem} ${brush === p.id ? recipe.paletteItemActive : ''}`}
              onClick={() => setBrush(p.id)}
            >
              {p.path ? <PackThumbnail groupId={p.groupId} path={p.path} size={34} /> : <span className={recipe.cellText}>{p.id.split(':').pop()}</span>}
            </button>
          ))}
        </div>
        <div className={recipe.brushBar}>
          <span>Pinceau</span>
          <input
            className={recipe.brushInput}
            value={brush}
            onChange={(e) => setBrush(e.target.value)}
            placeholder="clique un item ou tape un id (ex. minecraft:stick)"
            spellCheck={false}
          />
          <span className={recipe.brushHint}>clic gauche = poser · clic droit = dupliquer</span>
        </div>
      </div>

      {/* Zone de craft */}
      {craftType === 'classique' ? (
        <div className={recipe.craft}>
          <div className={recipe.grid}>
            {grid.map((cell, i) => {
              const info = infoOf.get(cell);
              return <Cell key={i} groupId={info?.groupId ?? groupId} item={cell} path={info?.path ?? null} onPlace={() => setCell(i, brush)} onGrab={() => cell && setBrush(cell)} />;
            })}
          </div>
          <ArrowRight className={recipe.arrow} size={28} />
          <div className={recipe.result}>
            <PackThumbnail groupId={groupId} path={thumbnail} size={56} alt={identifier} />
            <input className={recipe.count} type="number" min={1} max={64} value={count} onChange={(e) => { setCount(e.target.value); setDirty(true); }} />
            <span className={recipe.resultId}>{identifier}</span>
          </div>
        </div>
      ) : (
        <div className={recipe.craft}>
          <div className={recipe.furnaceCol}>
            <span className={recipe.slotLabel}>Entrée</span>
            <Cell groupId={infoOf.get(input)?.groupId ?? groupId} item={input} path={infoOf.get(input)?.path ?? null} onPlace={() => { setInput(brush); setDirty(true); }} onGrab={() => input && setBrush(input)} />
            <label className={recipe.xpLabel}>
              XP
              <input className={recipe.count} type="number" min={0} step={0.05} value={experience} onChange={(e) => { setExperience(e.target.value); setDirty(true); }} />
            </label>
          </div>
          <ArrowRight className={recipe.arrow} size={28} />
          <div className={recipe.result}>
            <PackThumbnail groupId={groupId} path={thumbnail} size={56} alt={identifier} />
            <span className={recipe.resultId}>{identifier}</span>
          </div>
        </div>
      )}

      {feedback && (
        <div className={feedback.ok ? styles.ok : styles.error}>
          {feedback.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {feedback.msg}
        </div>
      )}

      <div className={styles.actions}>
        <button className={styles.saveBtn} onClick={save} disabled={saving || !canSave}>
          {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />}
          {saving ? 'Enregistrement…' : 'Enregistrer la recette'}
        </button>
        <button className={styles.publishBtn} onClick={publish} disabled={publishing}>
          {publishing ? <Loader2 size={14} className={styles.spin} /> : <UploadCloud size={14} />}
          {publishing ? 'Publication…' : 'Publier sur git'}
        </button>
      </div>
    </div>
  );
}

/** Une case de craft : clic gauche = poser le pinceau, clic droit = récupérer l'item (dupliquer). */
function Cell({
  groupId,
  item,
  path,
  onPlace,
  onGrab,
}: {
  groupId: string;
  item: string;
  path: string | null;
  onPlace: () => void;
  onGrab: () => void;
}) {
  return (
    <button
      type="button"
      className={recipe.cell}
      title={item || undefined}
      onClick={onPlace}
      onContextMenu={(e) => {
        e.preventDefault();
        onGrab();
      }}
    >
      {item ? (
        path ? <PackThumbnail groupId={groupId} path={path} size={56} /> : <span className={recipe.cellText}>{item.split(':').pop()}</span>
      ) : null}
    </button>
  );
}
